import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatAmount } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  Input,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { IconButton } from '../../ui/components/Button';
import { Icon } from '../../ui/icons';
import { useT } from '../../i18n';
import { recipesApi } from './recipesApi';
import { menuApi, menuClassMeta } from './menuApi';
import './menu.css';

let tmpId = 0;
const tmpKey = () => `ma-${++tmpId}`;
const num = (v) => { const n = parseFloat(v); return Number.isNaN(n) ? null : n; };

// Quadrant layout: profitability high on top; popularity high on the right.
const QUADRANTS = [
  ['Puzzle', 'Star'],
  ['Dog', 'Plowhorse'],
];

export default function MenuAnalysisFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const [menuItems, setMenuItems] = useState([]);
  const [name, setName] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState([]);

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const previewSeq = useRef(0);

  const loadLookups = useCallback(async () => {
    try {
      const recs = await recipesApi.list({ type: 'MenuItem' });
      setMenuItems(recs || []);
    } catch {
      setMenuItems([]);
    }
  }, []);

  const loadAnalysis = useCallback(async () => {
    if (!editing) { setLoading(false); return; }
    setLoading(true);
    try {
      const a = await menuApi.get(id);
      setName(a.name || '');
      setPeriodStart(a.periodStart ? a.periodStart.slice(0, 10) : '');
      setPeriodEnd(a.periodEnd ? a.periodEnd.slice(0, 10) : '');
      setNotes(a.notes || '');
      setLines((a.lines || []).map((l) => ({ key: tmpKey(), recipeId: l.recipeId, name: l.name, unitsSold: String(l.unitsSold ?? '') })));
      setPreview(a);
    } catch (err) {
      toast.error(t('Could not load analysis'), { description: err?.message });
      navigate('/menu-engineering');
    } finally {
      setLoading(false);
    }
  }, [editing, id, navigate, toast, t]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { loadAnalysis(); }, [loadAnalysis]);

  const usedIds = useMemo(() => new Set(lines.map((l) => l.recipeId)), [lines]);
  const itemOptions = useMemo(
    () => menuItems
      .filter((m) => !usedIds.has(m.id))
      .map((m) => ({ value: m.id, label: m.name, description: m.number })),
    [menuItems, usedIds],
  );

  const addItem = (recipeId) => {
    const m = menuItems.find((x) => x.id === recipeId);
    if (!m) return;
    setLines((prev) => [...prev, { key: tmpKey(), recipeId, name: m.name, unitsSold: '' }]);
  };
  const updateUnits = (key, units) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, unitsSold: units } : l)));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  // Debounced live preview whenever the lines change.
  const linesSignature = useMemo(
    () => JSON.stringify(lines.map((l) => [l.recipeId, num(l.unitsSold) ?? 0])),
    [lines],
  );
  useEffect(() => {
    if (lines.length === 0) { setPreview((p) => (editing ? p : null)); return undefined; }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    const timer = setTimeout(async () => {
      try {
        const payload = lines
          .filter((l) => l.recipeId)
          .map((l) => ({ recipeId: l.recipeId, unitsSold: num(l.unitsSold) ?? 0 }));
        const res = await menuApi.preview(payload);
        if (seq === previewSeq.current) setPreview(res);
      } catch {
        /* keep last preview */
      } finally {
        if (seq === previewSeq.current) setPreviewing(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesSignature]);

  const currency = preview?.currency || 'USD';
  const fmtMoney = (n) => `${currency} ${formatAmount(n)}`;
  const summary = preview?.summary;
  const resultLines = preview?.lines || [];
  const byClass = (cls) => resultLines.filter((l) => !l.missing && l.classification === cls);

  const buildPayload = () => ({
    name: name.trim(),
    periodStart: periodStart ? new Date(periodStart).toISOString() : null,
    periodEnd: periodEnd ? new Date(periodEnd).toISOString() : null,
    notes: notes.trim() || null,
    lines: lines.filter((l) => l.recipeId).map((l) => ({ recipeId: l.recipeId, unitsSold: num(l.unitsSold) ?? 0 })),
  });

  const save = async () => {
    if (!name.trim()) { toast.error(t('Enter a name for the analysis.')); return; }
    if (lines.filter((l) => l.recipeId).length === 0) { toast.error(t('Add at least one menu item.')); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (editing) {
        const updated = await menuApi.update(id, payload);
        setPreview(updated);
        toast.success(t('Analysis saved'));
      } else {
        const created = await menuApi.create(payload);
        toast.success(t('{number} created', { number: created.number }));
        navigate(`/menu-engineering/${created.id}`);
      }
    } catch (err) {
      toast.error(t('Could not save analysis'), { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const doDelete = async () => {
    setSubmitting(true);
    try {
      await menuApi.remove(id);
      toast.success(t('Analysis deleted'));
      navigate('/menu-engineering');
    } catch (err) {
      toast.error(t('Could not delete analysis'), { description: err?.message });
      setDeleteOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/menu-engineering')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Menu engineering')}
      </Button>

      <PageHeader
        title={editing ? (name || t('Menu analysis')) : t('New menu analysis')}
        description={t('Enter units sold per menu item. Margins are computed live from current recipe costs and items are classified into the menu-engineering matrix.')}
        icon={<Icon.LayoutGrid size={22} />}
        actions={
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            {editing && (
              <Button variant="destructive" disabled={submitting} leadingIcon={<Icon.Trash size={15} />} onClick={() => setDeleteOpen(true)}>{t('Delete')}</Button>
            )}
            <Button variant="primary" loading={submitting} leadingIcon={<Icon.Check size={15} />} onClick={save}>
              {editing ? t('Save') : t('Create analysis')}
            </Button>
          </div>
        }
      />

      <div className="me-layout">
        <div className="me-main">
          <Card>
            <Card.Body>
              <div className="me-form">
                <Input label={t('Name')} placeholder={t('e.g. Dinner menu — Q2')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                <div className="me-grid">
                  <Input label={t('Period start')} type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
                  <Input label={t('Period end')} type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
                </div>
                <Textarea label={t('Notes')} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('Context for this study (optional)')} />
              </div>
            </Card.Body>
          </Card>

          <Card>
            <Card.Header>
              <Card.Title>{t('Menu items & units sold')}</Card.Title>
              {previewing && <span className="me-muted" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Spinner size={14} /> {t('Updating…')}</span>}
            </Card.Header>
            <Card.Body>
              <div className="me-add">
                <Icon.Plus size={16} />
                <Combobox
                  options={itemOptions}
                  value=""
                  onChange={addItem}
                  placeholder={t('Add a menu item…')}
                  searchPlaceholder={t('Search menu items…')}
                  size="sm"
                  emptyText={menuItems.length === 0 ? t('No menu items — create some in Recipes first') : t('All menu items added')}
                  ariaLabel={t('Add menu item')}
                />
              </div>

              {lines.length === 0 ? (
                <div className="me-empty">{t('Add menu items to begin. Only recipes of type “Menu item” with a selling price can be analysed.')}</div>
              ) : (
                <div className="me-lines">
                  <div className="me-line me-line--head">
                    <span>{t('Item')}</span>
                    <span>{t('Units sold')}</span>
                    <span className="me-num">{t('Mix %')}</span>
                    <span className="me-num">{t('CM')}</span>
                    <span>{t('Class')}</span>
                    <span />
                  </div>
                  {lines.map((l) => {
                    const r = resultLines.find((x) => x.recipeId === l.recipeId);
                    const meta = r && !r.missing ? menuClassMeta(r.classification) : null;
                    return (
                      <div className="me-line" key={l.key}>
                        <div className="me-line__name">
                          {l.name}
                          {r?.missing && <Badge tone="danger">{t('unpriced')}</Badge>}
                          {r?.overTarget && <Badge tone="warning">{t('over target')}</Badge>}
                        </div>
                        <Input size="sm" type="number" value={l.unitsSold} placeholder="0" onChange={(e) => updateUnits(l.key, e.target.value)} />
                        <span className="me-num">{r && !r.missing ? `${r.mixPercent.toFixed(1)}%` : '—'}</span>
                        <span className="me-num">{r && !r.missing ? fmtMoney(r.contributionMargin) : '—'}</span>
                        <span>{meta ? <Badge tone={meta.tone}>{t(meta.label)}</Badge> : <span className="me-muted">—</span>}</span>
                        <IconButton aria-label={t('Remove')} variant="ghost" size="icon-sm" onClick={() => removeLine(l.key)}><Icon.Trash size={15} /></IconButton>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card.Body>
          </Card>

          {summary && summary.itemCount > 0 && (
            <Card>
              <Card.Header><Card.Title>{t('Menu matrix')}</Card.Title><Card.Subtitle>{t('Popularity → · Profitability ↑')}</Card.Subtitle></Card.Header>
              <Card.Body>
                <div className="me-matrix">
                  {QUADRANTS.map((row, ri) => row.map((cls) => {
                    const meta = menuClassMeta(cls);
                    const items = byClass(cls);
                    return (
                      <div className={`me-cell me-cell--${cls.toLowerCase()}`} key={cls}>
                        <div className="me-cell__head">
                          <Badge tone={meta.tone}>{t(meta.label)}</Badge>
                          <span className="me-cell__count">{items.length}</span>
                        </div>
                        <div className="me-cell__hint">{t(meta.hint)}</div>
                        <ul className="me-cell__list">
                          {items.map((it) => (
                            <li key={it.recipeId}>
                              <span className="me-cell__item">{it.name}</span>
                              <span className="me-cell__meta">{it.mixPercent.toFixed(1)}% · {fmtMoney(it.contributionMargin)}</span>
                            </li>
                          ))}
                          {items.length === 0 && <li className="me-muted">—</li>}
                        </ul>
                      </div>
                    );
                  }))}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>

        <div className="me-side">
          <Card>
            <Card.Header><Card.Title>{t('Summary')}</Card.Title></Card.Header>
            <Card.Body>
              {summary && summary.itemCount > 0 ? (
                <div className="me-summary">
                  <KpiTile label={t('Total contribution')} value={fmtMoney(summary.totalContribution)} meta={t('{count} units', { count: summary.totalUnits })} />
                  <KpiTile label={t('Avg contribution / cover')} value={fmtMoney(summary.averageContributionMargin)} meta={t('Profitability threshold')} />
                  <KpiTile
                    label={t('Avg food cost %')}
                    value={summary.averageFoodCostPercent == null ? '—' : `${summary.averageFoodCostPercent.toFixed(1)}%`}
                  />
                  <KpiTile label={t('Popularity threshold')} value={`${summary.popularityThresholdPercent.toFixed(1)}%`} meta={t('70% rule (menu mix)')} />
                  <div className="me-counts">
                    <span><Badge tone="success">{t('Stars')} {summary.stars}</Badge></span>
                    <span><Badge tone="warning">{t('Plowhorses')} {summary.plowhorses}</Badge></span>
                    <span><Badge tone="info">{t('Puzzles')} {summary.puzzles}</Badge></span>
                    <span><Badge tone="danger">{t('Dogs')} {summary.dogs}</Badge></span>
                  </div>
                </div>
              ) : (
                <div className="me-muted">{t('Add menu items with units sold to see the analysis.')}</div>
              )}
            </Card.Body>
          </Card>

          {resultLines.some((l) => !l.missing) && (
            <Card>
              <Card.Header><Card.Title>{t('Recommendations')}</Card.Title></Card.Header>
              <Card.Body>
                <ul className="me-recs">
                  {resultLines.filter((l) => !l.missing).map((l) => {
                    const meta = menuClassMeta(l.classification);
                    return (
                      <li key={l.recipeId}>
                        <div className="me-recs__top">
                          <span className="me-recs__name">{l.name}</span>
                          <Badge tone={meta.tone}>{t(meta.label)}</Badge>
                        </div>
                        <div className="me-recs__text">{l.recommendation}</div>
                      </li>
                    );
                  })}
                </ul>
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={deleteOpen}
        title={t('Delete analysis')}
        description={t('This permanently removes the menu-engineering analysis.')}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={submitting}
        onConfirm={doDelete}
        onCancel={() => (submitting ? null : setDeleteOpen(false))}
      />
    </PageContainer>
  );
}
